/**
 * Swiss UID (Unternehmens-Identifikationsnummer) Validation
 *
 * Implements the eCH-0097 standard for Swiss business identification numbers.
 * Format: CHE-XXX.XXX.XXP where P is the check digit (Modulo 11)
 *
 * @see https://www.uid.admin.ch
 * @see https://www.ech.ch (eCH-0097 standard)
 */

/**
 * Result of UID validation
 */
export interface UidValidationResult {
  /** Whether the UID is valid */
  valid: boolean;
  /** Formatted UID (CHE-XXX.XXX.XXX) or null if invalid */
  formatted: string | null;
  /** Error message if invalid */
  error?: string;
  /** Raw 9 digits extracted from input */
  digits?: string;
}

/**
 * Regex for strict format validation: CHE-XXX.XXX.XXX
 */
export const UID_STRICT_REGEX = /^CHE-\d{3}\.\d{3}\.\d{3}$/;

/**
 * Regex for flexible input parsing (handles various formats)
 * Matches: CHE-123.456.789, CHE123456789, 123.456.789, 123456789
 */
export const UID_INPUT_REGEX = /^(CHE)?[\s\-.]?(\d{3})[\s\-.]?(\d{3})[\s\-.]?(\d{3})$/i;

/**
 * Weights for Modulo 11 check digit calculation per eCH-0097
 */
const CHECK_DIGIT_WEIGHTS = [5, 4, 3, 2, 7, 6, 5, 4];

/**
 * Calculate the check digit for a Swiss UID using Modulo 11 algorithm
 *
 * @param digits - First 8 digits of the UID
 * @returns Calculated check digit (0-9) or null if invalid (result is 10)
 */
export function calculateCheckDigit(digits: string): number | null {
  if (!/^\d{8}$/.test(digits)) {
    return null;
  }

  let sum = 0;
  for (let i = 0; i < 8; i++) {
    sum += parseInt(digits[i], 10) * CHECK_DIGIT_WEIGHTS[i];
  }

  const remainder = sum % 11;
  const checkDigit = (11 - remainder) % 11;

  // If check digit would be 10, the UID is invalid per eCH-0097
  if (checkDigit === 10) {
    return null;
  }

  return checkDigit;
}

/**
 * Validate a Swiss UID's check digit
 *
 * @param digits - All 9 digits of the UID
 * @returns true if check digit is valid
 */
export function validateCheckDigit(digits: string): boolean {
  if (!/^\d{9}$/.test(digits)) {
    return false;
  }

  const calculatedCheckDigit = calculateCheckDigit(digits.slice(0, 8));
  if (calculatedCheckDigit === null) {
    return false;
  }

  const actualCheckDigit = parseInt(digits[8], 10);
  return calculatedCheckDigit === actualCheckDigit;
}

/**
 * Format a UID string into the standard display format: CHE-XXX.XXX.XXX
 *
 * @param uid - UID in any recognized format
 * @returns Formatted UID or null if input is invalid
 */
export function formatUid(uid: string): string | null {
  if (!uid || typeof uid !== 'string') {
    return null;
  }

  const cleaned = uid.toUpperCase().replace(/\s/g, '');
  const match = cleaned.match(UID_INPUT_REGEX);

  if (!match) {
    return null;
  }

  const digits = match[2] + match[3] + match[4];
  return `CHE-${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}`;
}

/**
 * Parse a UID string and extract the 9 digits
 *
 * @param uid - UID in any recognized format
 * @returns 9 digit string or null if invalid
 */
export function parseUidDigits(uid: string): string | null {
  if (!uid || typeof uid !== 'string') {
    return null;
  }

  const cleaned = uid.toUpperCase().replace(/\s/g, '');
  const match = cleaned.match(UID_INPUT_REGEX);

  if (!match) {
    return null;
  }

  return match[2] + match[3] + match[4];
}

/**
 * Comprehensive Swiss UID validation
 *
 * Validates:
 * 1. Format: Must contain CHE prefix and 9 digits
 * 2. Check digit: Modulo 11 algorithm per eCH-0097
 *
 * @param uid - UID to validate (flexible input format accepted)
 * @returns Validation result with formatted UID and error details
 *
 * @example
 * ```typescript
 * validateSwissUid('CHE-123.456.788') // { valid: true, formatted: 'CHE-123.456.788' }
 * validateSwissUid('CHE123456788')    // { valid: true, formatted: 'CHE-123.456.788' }
 * validateSwissUid('CHE-123.456.789') // { valid: false, error: 'Invalid check digit...' }
 * ```
 */
export function validateSwissUid(uid: string): UidValidationResult {
  // Handle empty/null input
  if (!uid || typeof uid !== 'string') {
    return {
      valid: false,
      formatted: null,
      error: 'UID is required',
    };
  }

  // Trim and normalize
  const cleaned = uid.trim().toUpperCase().replace(/\s+/g, '');

  // Empty after cleaning
  if (!cleaned) {
    return {
      valid: false,
      formatted: null,
      error: 'UID is required',
    };
  }

  // Parse the input
  const match = cleaned.match(UID_INPUT_REGEX);

  if (!match) {
    return {
      valid: false,
      formatted: null,
      error: 'Invalid format. Expected: CHE-XXX.XXX.XXX (9 digits)',
    };
  }

  // Extract and validate digits
  const digits = match[2] + match[3] + match[4];

  // Format properly
  const formatted = `CHE-${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}`;

  // Calculate expected check digit
  const calculatedCheckDigit = calculateCheckDigit(digits.slice(0, 8));

  // Check digit calculation resulted in 10 (invalid UID)
  if (calculatedCheckDigit === null) {
    return {
      valid: false,
      formatted,
      digits,
      error: 'Invalid UID: this number combination cannot have a valid check digit',
    };
  }

  // Compare with actual check digit
  const actualCheckDigit = parseInt(digits[8], 10);

  if (calculatedCheckDigit !== actualCheckDigit) {
    return {
      valid: false,
      formatted,
      digits,
      error: `Invalid check digit. The last digit should be ${calculatedCheckDigit}, not ${actualCheckDigit}`,
    };
  }

  // All validations passed
  return {
    valid: true,
    formatted,
    digits,
  };
}

/**
 * Zod refinement function for use with react-hook-form
 *
 * @example
 * ```typescript
 * const schema = z.object({
 *   uid: z.string().refine(isValidSwissUid, {
 *     message: 'Invalid Swiss UID format or check digit'
 *   })
 * });
 * ```
 */
export function isValidSwissUid(uid: string): boolean {
  return validateSwissUid(uid).valid;
}

/**
 * Generate a valid UID from 8 digits by calculating the check digit
 *
 * @param digits - First 8 digits
 * @returns Complete 9-digit UID string or null if invalid
 */
export function generateUidWithCheckDigit(digits: string): string | null {
  if (!/^\d{8}$/.test(digits)) {
    return null;
  }

  const checkDigit = calculateCheckDigit(digits);
  if (checkDigit === null) {
    return null;
  }

  return digits + checkDigit.toString();
}

/**
 * Format UID for display with proper spacing and separators
 *
 * @param uid - UID in any format
 * @returns Formatted display string or original input if invalid
 */
export function formatUidForDisplay(uid: string): string {
  const formatted = formatUid(uid);
  return formatted || uid;
}

/**
 * Format UID for transmission (no separators): CHE123456789
 *
 * @param uid - UID in any format
 * @returns Transmission format string or null if invalid
 */
export function formatUidForTransmission(uid: string): string | null {
  const digits = parseUidDigits(uid);
  if (!digits) {
    return null;
  }
  return `CHE${digits}`;
}
